/** Amounts from API are in kobo (₦1 = 100). */

export function resolveWalletAccountNumber(wallet) {
  if (!wallet) return "";
  return String(wallet.accountNumber ?? wallet.account_number ?? "").trim();
}

export function resolveWalletBankName(wallet) {
  if (!wallet) return "Safe Haven MFB";
  return wallet.bankName || wallet.bank_name || "Safe Haven MFB";
}

export function isVirtualAccountPending(wallet, { loading = false } = {}) {
  if (loading) return false;
  if (!wallet) return true;
  if (wallet.virtualAccountPending === true) return true;
  if (wallet.virtualAccountPending === false) return false;
  return !resolveWalletAccountNumber(wallet);
}

export function koboToNaira(kobo) {
  return (Number(kobo) || 0) / 100;
}

export function formatNairaFromKobo(kobo) {
  const n = koboToNaira(kobo);
  return `₦${n.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Live "Valid for …" label until `expiresAt`; returns "Expired" when past. */
export function formatValidForCountdown(expiresAt, nowMs = Date.now()) {
  if (!expiresAt) return null;
  const end = new Date(expiresAt).getTime();
  if (Number.isNaN(end)) return null;
  const remainingMs = end - nowMs;
  if (remainingMs <= 0) return "Expired";
  const totalSec = Math.floor(remainingMs / 1000);
  const hours = Math.floor(totalSec / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  if (hours > 0) {
    return `Valid for ${hours}h ${mins}m`;
  }
  return `Valid for ${mins}:${String(secs).padStart(2, "0")}`;
}

/** Compact label for cards (no decimals). */
export function formatNairaCompactFromKobo(kobo) {
  const n = koboToNaira(kobo);
  return `₦${Math.round(n).toLocaleString("en-NG")}`;
}

const TX_INCOMING = new Set([
  "credit",
  "topup",
  "wallet_topup",
  "contract_release",
  "contract_return",
  "withdrawal_reversal",
  "deal_funding",
  "p2p_receive",
  "ticket_sale",
]);

const TX_OUTGOING = new Set([
  "withdrawal",
  "contract_lock",
  "deal_funding_reserve",
  "p2p_send",
  "ticket_purchase",
  "debit",
]);

const TX_FEE = new Set(["nip_fee", "fee", "ticket_platform_fee", "inactivity_fee"]);

/** Types that can credit or debit — resolve from balance_before/after. */
const TX_BALANCE_DIRECTION = new Set([
  "ticket_reversal",
  "balance_reconciliation",
  "p2p_reversal",
  "deal_funding_release",
  "nip_fee_adjustment",
]);

const TYPE_LABELS = {
  credit: "Wallet credit",
  topup: "Wallet top-up",
  wallet_topup: "Wallet top-up",
  withdrawal: "Withdrawal",
  withdrawal_reversal: "Withdrawal reversed",
  contract_lock: "Contract funded",
  contract_release: "Milestone released",
  contract_return: "Contract refund",
  deal_funding: "Deal funding",
  deal_funding_release: "Deal funding returned",
  nip_fee: "NIP transfer fee",
  nip_fee_adjustment: "Transfer fee adjustment",
  balance_reconciliation: "Balance synced with bank",
  inactivity_fee: "Inactivity fee",
  p2p_send: "Transfer sent",
  p2p_receive: "Transfer received",
  p2p_reversal: "Transfer reversed",
  ticket_purchase: "Ticket purchase",
  ticket_sale: "Ticket sale",
  ticket_platform_fee: "Ticket fee",
  ticket_reversal: "Ticket payment reversed",
  debit: "Debit",
  fee: "Fee",
};

/** Legacy wallet ticket pays were stored as p2p_* with TICKET- clientReference. */
function resolveDisplayType(tx) {
  const type = tx.type || "";
  let meta = tx.metadata;
  if (typeof meta === "string") {
    try {
      meta = JSON.parse(meta);
    } catch {
      meta = {};
    }
  }
  const clientRef = meta && typeof meta === "object" ? String(meta.clientReference || "") : "";
  if (clientRef.startsWith("TICKET-")) {
    if (type === "p2p_send") return "ticket_purchase";
    if (type === "p2p_receive") return "ticket_sale";
    if (type === "p2p_reversal") return "ticket_reversal";
  }
  return type;
}

export function formatTxDate(createdAt) {
  if (!createdAt) return "";
  const dt = new Date(createdAt);
  if (Number.isNaN(dt.getTime())) return "";
  const now = new Date();
  if (dt.toDateString() === now.toDateString()) {
    return dt.toLocaleTimeString("en-NG", { hour: "numeric", minute: "2-digit" });
  }
  const opts = { month: "short", day: "numeric" };
  if (dt.getFullYear() !== now.getFullYear()) opts.year = "numeric";
  return dt.toLocaleDateString("en-NG", opts);
}

function resolveIncoming(type, tx) {
  const hasBalances = tx.balance_before != null && tx.balance_after != null;
  if (hasBalances && (TX_BALANCE_DIRECTION.has(type) || (!TX_INCOMING.has(type) && !TX_OUTGOING.has(type) && !TX_FEE.has(type)))) {
    return Number(tx.balance_after) > Number(tx.balance_before);
  }
  return TX_INCOMING.has(type);
}

/** Wallet ledger row from GET /wallet — snake_case fields from pg */
export function mapWalletTransaction(tx) {
  const rawType = tx.type || "";
  const type = resolveDisplayType(tx);
  const isFee = TX_FEE.has(type);
  const incoming = isFee ? false : resolveIncoming(type, tx);
  const dir = isFee ? "fee" : incoming ? "in" : "out";
  const sign = incoming ? "+" : "−";
  const n = koboToNaira(tx.amount);
  const displayAmount = isFee || type === "balance_reconciliation"
    ? n.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : Math.round(n).toLocaleString("en-NG");
  const status = tx.status || "completed";
  const typeLabel = TYPE_LABELS[type] || type.replace(/_/g, " ") || "Ledger";
  const looksLikeP2pTransfer = /^Transfer (to|from) @/i.test(String(tx.description || ""));
  const name = type !== rawType && looksLikeP2pTransfer
    ? typeLabel
    : (tx.description || typeLabel || "Transaction");

  return {
    id: tx.id || tx.reference || "",
    name,
    amt: `${sign}₦${displayAmount}`,
    dir,
    cat: typeLabel,
    ref: tx.reference || "",
    date: formatTxDate(tx.created_at),
    status,
    rawType: type,
  };
}

/** @deprecated use mapWalletTransaction */
export const mapLedgerRowForHome = mapWalletTransaction;
